import moment from 'moment-timezone';

import { getText } from 'function/language';
import { isEmpty, isNumber, isObject, valueFrom } from 'utils';
import { comma, number as formatNumber } from 'utils/format';

/**
 * 배열 항목을 값 - 텍스트 객체로 변환
 * @param {Object[]} array 배열
 * @param {string} valueKey 값에 대한 키
 * @param {string} [textKey] 텍스트에 대한 키
 * @return {Object[]} 값 - 텍스트 객체 항목의 배열
 */
function array2ValueAndText(array = [], valueKey, textKey) {
  return array.map((value = {}) => ({
    text: valueFrom(value[textKey], value[valueKey]),
    value: value[valueKey]
  }));
}

/**
 * 하위 컴포넌트 목록을 키 - 컴포넌트 객체로 변환
 * @param {Object[] | Object} childrens 하위 컴포넌트 목록
 * @return {Object} 키 - 컴포넌트 객체
 */
function childrenComponent2Object(childrens) {
  if (isObject(childrens)) {
    return { [childrens.key]: childrens };
  }

  return childrens.reduce(
    (result, value = {}) => ({
      ...result,
      [value.key]: value
    }),
    {}
  );
}

/**
 * 데이터 목록을 엑셀 배열로 변환
 * @param {Object[]} datas 데이터 목록
 * @param {string[]} keys 데이터 목록 항목의 키 목록
 * @return {Array[]} 엑셀 배열
 */
function datas2ExcelArray(datas = [], keys = []) {
  return datas.map((value = {}) => keys.map(key => value[key]));
}

/**
 * 데이터 목록을 엑셀 JSON으로 변환
 * @param {Object[]} datas 데이터 목록
 * @param {Object} columns 컬럼 목록
 * @return {Object[]} 엑셀 JSON
 */
function datas2ExcelJSON(datas = [], columns = {}) {
  return datas.map((value = {}) =>
    Object.entries(columns).reduce(
      (result, [key, { title } = {}]) => ({
        ...result,
        [getText(title ?? key)]: value[key]
      }),
      {}
    )
  );
}

/**
 * 선진 SHSH 데이터 목록을 엑셀 JSON으로 변환
 * @param {Object[]} datas 데이터 목록
 * @param {Object} columns 컬럼 목록
 * @return {Object[]} 엑셀 JSON
 */
function datas2SfshExcelJSON(datas = [], columns = {}) {
  return datas.map((value = {}) =>
    Object.entries(columns).reduce(
      (result, [key, title]) => ({
        ...result,
        [title]: value[key]
      }),
      {}
    )
  );
}

/**
 * 숫자를 형식대로 변환
 * @param {number | string} value 값
 * @param {string} [format] 형식
 * @param {number} [max] 최대 값
 * @returns
 */
function number2format(value, format, max) {
  value = String(value);

  const isNegative = value.startsWith('-');
  const isEndsWithPeriod = value.endsWith('.');
  const [, precision = ''] = value.split('.');
  const [, precisionFormat = ''] = format.split('.');
  const { length: precisionLength } = precision;

  if (value.split('-').length > 1 && !isNegative) {
    return false;
  }

  if (value === '.') {
    return false;
  }

  if (value.split('.').length > 2) {
    return false;
  }

  const number = Number(value.replaceAll(',', ''));

  if (!isNumber(number) && number !== '-' && !isEndsWithPeriod) {
    return false;
  }

  if (value && max < number) {
    return false;
  }

  if (
    value &&
    !isNegative &&
    !isEndsWithPeriod &&
    (precisionLength === 0 || precisionLength >= precisionFormat.length)
  ) {
    value = format ? formatNumber(number, format) : comma(number);
  }

  return value;
}

/**
 * 문자열 양 끝의 공백 제거 (trim)
 * @param {string} string 문자열
 * @return {string} 양 끝의 공백이 제거된 문자열
 */
function string2trim(string) {
  return typeof string === 'string' ? string.trim() : string;
}

/**
 * UTC 시간을 표준 시간대가 적용된 시간으로 변환
 * @param {string/number} utc UTC 시간
 * @param {string} [format='YYYY-MM-DD HH:mm:ss'] 시간 형식
 * @return {string} 표준 시간대가 적용된 시간
 */
function utc2tz(utc, format = 'YYYY-MM-DD HH:mm:ss') {
  return !isEmpty(utc) && utc > 0
    ? moment.utc(utc).tz(moment.tz.guess()).format(format)
    : '';
}

export {
  array2ValueAndText,
  childrenComponent2Object,
  datas2ExcelArray,
  datas2ExcelJSON,
  datas2SfshExcelJSON,
  number2format,
  string2trim,
  utc2tz
};
